// src/components/App/AppContent.js
import React, { useEffect, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import Header from "../../layout/Header/Header";
import Footer from "../../layout/Footer/Footer";
import Sidebar from "../../UserDashbaord/layout/Sidebar";
import routes from "../../Routes/AppRoutes";
import { Box } from "@mui/material";
import { Helper, isLogin } from "../../tools/Helper";
import { api_Routes } from "../../api_Routes";
import Header2 from "../common/header";
import { useDispatch, useSelector } from "react-redux";
import {
    setIsLoading,
    setIsSettingsChanges,
    setProfileData,
} from "../../store/slices/profileSlice";
import GoogleTag from "../../helpers/GoogleTags";
import FloatingChatButton from "./FloatingChatButton";
import ScrollToTop from "../ScrollToTop";
import { useSlider } from "../../Context/SliderContext";
import MainSlider from "../slider/MainSlider";
import News from "../Home/News";

function GtagEvent() {
    useEffect(() => {
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.innerHTML = `
      gtag('event', 'conversion', {
        'send_to': 'AW-11547846789/2IcdCJHI8YsaEIXRuIIr',
        'value': 1.0,
        'currency': 'EUR'
      });
    `;
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    return null;
}

function AppContent() {
    const dispatch = useDispatch();
    const location = useLocation();
    const { sliders, loading } = useSlider();

    // Initialize news state from localStorage with expiry check
    const itemStr = localStorage.getItem("news");
    let cachedNews = null;
    if (itemStr) {
        try {
            const item = JSON.parse(itemStr);
            const now = new Date();
            if (now.getTime() < item.expiry) {
                cachedNews = item.value;
            } else {
                localStorage.removeItem("news");
            }
        } catch (error) {
            // In case JSON parsing fails, remove corrupted item
            localStorage.removeItem("news");
        }
    }
    const [news, setNews] = useState(cachedNews);

    // Listen for localStorage changes from other tabs/windows
    useEffect(() => {
        function handleStorageChange(event) {
            if (event.key === "news") {
                if (event.newValue) {
                    try {
                        const item = JSON.parse(event.newValue);
                        const now = new Date();
                        if (now.getTime() < item.expiry) {
                            setNews(item.value);
                        } else {
                            localStorage.removeItem("news");
                            setNews(null);
                        }
                    } catch {
                        localStorage.removeItem("news");
                        setNews(null);
                    }
                } else {
                    // news key was removed
                    setNews(null);
                }
            }
        }

        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, []);

    // Fetch news from API and update state + localStorage with 1 hour expiry
    const get_news = async () => {
        const { response, message } = await Helper.Get({
            url: api_Routes.HomePage.news,
            data: { results: 100 },
        });
        if (response) {
            const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
            const now = new Date();

            const newsWithExpiry = {
                value: response.data,
                expiry: now.getTime() + oneHour,
            };

            localStorage.setItem("news", JSON.stringify(newsWithExpiry));
            setNews(response.data); // Update React state immediately
        }
    };

    useEffect(() => {
        if (!news) {
            get_news();
        }
    }, [news]);

    // Profile related state and fetch
    const isDashboard = location.pathname.startsWith("/dashboard");
    const isChat = location.pathname.startsWith("/chat");

    const profileData = useSelector((state) => state.profile.profileData);
    const isProfileDataLoading = useSelector((state) => state.profile.isLoading);
    const isProfileDataSettingsChanges = useSelector(
        (state) => state.profile.isSettingsChanges
    );

    const [isOpen, setIsOpen] = useState(true);
    const [isSmallScreenIndicator, setIsSmallScreenIndicator] = useState(
        window.innerWidth < 1300
    );

    const get_profile = async () => {
        const { response, message } = await Helper.Get({
            url: api_Routes.profile.showprofile,
            hasToken: true,
            data: { results: 100 },
        });
        if (response) {
            dispatch(
                setProfileData({
                    id: response.user.id,
                    name: response.user.name,
                    email: response.user.email,
                    photo: response.user.photo,
                    description: response.user.description,
                    whatsapp: response.user.whatsapp,
                    telegram: response.user.telegram,
                    instagram: response.user.instagram,
                    facebook: response.user.facebook,
                    skype: response.user.skype,
                    tiktok: response.user.tiktok,
                    sex: response.user.sex,
                    about: response.user.about,
                    country: response.user.country,
                    notify_by_push: response.notify_by_push,
                    notify_by_email: response.notify_by_email,
                })
            );
        }
        dispatch(setIsLoading());
    };

    useEffect(() => {
        if ((!profileData || isProfileDataSettingsChanges) && isLogin) {
            get_profile();
        }
    }, [location]);

    useEffect(() => {
        if (isSmallScreenIndicator) setIsOpen(false);
    }, []);

    // Handle window resize to update small screen indicator
    useEffect(() => {
        const handleResize = () => {
            const isSmallScreen = window.innerWidth <= 1300;
            setIsSmallScreenIndicator(isSmallScreen);
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    return (
        <main
            className="content"
            style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
        >
            <GoogleTag />
            <GtagEvent />
            <ScrollToTop />
            <Header />

            {!isDashboard && !isChat && (
                <>
                    <News trusted={news} />
                    <Header2 />
                    {!loading && sliders.length > 0 && <MainSlider sliders={sliders} />}
                </>
            )}

            <Box
                sx={{
                    display: isDashboard ? "flex" : undefined,
                    flex: isDashboard ? 1 : undefined,
                    minHeight: {
                        sm: "calc(100vh - 60px)",
                        md: "calc(100vh - 60px)",
                        lg: "calc(100vh - 60px)",
                    },
                }}
            >
                {isDashboard && <Sidebar isOpen={isOpen} setIsOpen={setIsOpen} />}
                <div
                    style={{
                        width: isDashboard
                            ? isSmallScreenIndicator
                                ? "100%"
                                : isOpen
                                    ? "calc(100% - 250px)"
                                    : "100%"
                            : "100%",
                        paddingInlineStart: isDashboard
                            ? !isSmallScreenIndicator
                                ? isOpen
                                    ? "250px"
                                    : "0px"
                                : "0px"
                            : "0px",
                        transition: "all 0.3s ease-in-out",
                        height: "100%",
                    }}
                >
                    <Routes>
                        {routes.map(({ path, element, children }, index) => (
                            <Route key={index} path={path} element={element}>
                                {children &&
                                children.map(({ path, element }, childIndex) => (
                                    <Route key={childIndex} path={path} element={element} />
                                ))}
                            </Route>
                        ))}
                    </Routes>
                </div>
            </Box>
            <FloatingChatButton />
            <Footer />
        </main>
    );
}

export default AppContent;
